/**
 * PrimaryStudySelect — the study a patient is being CONSIDERED for, shown under
 * their name and editable inline. A compact select (primary when set, amber when
 * none) that PATCHes /api/crm/contacts/:id { primary_study_id } itself
 * (optimistic, reverts on failure). Safe inside clickable rows.
 *
 * The CRM study list is fetched on mount (source==='crm', adoptable id present);
 * the passed studyTitle seeds the label so the current study shows before the
 * list lands. The backend also auto-sets this when a recruiter starts a
 * prescreen, so this stays in sync with the screener flow.
 *
 * When the lead already sits in a STUDY-ATTRIBUTED stage (Screened / Randomized
 * / Screen Failed — pass `lifecycleStatus`), changing the study first asks
 * whether the recorded outcome should move with it: "fix reports too" sends
 * reattribute_current_stage:true so the backend re-points the stage-events
 * ledger row (the repair path for a lead whose outcome landed under the wrong
 * study — e.g. screened for study B while still bucketed under A).
 */
import { useEffect, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import api from '../../services/api';
import { StudyOption, studyInternalLabel } from '../types';
import { STUDY_ATTRIBUTED_STAGES, lifecycleMeta } from '../lib';

interface Props {
  contactId: string;
  studyId: string | null | undefined;
  /** Resolved label for the current study (so the chip reads right pre-load). */
  studyTitle?: string | null;
  /** The lead's current pipeline status — enables the "fix reports too" prompt
   *  when changing the study of an already-screened/randomized/screen-failed lead. */
  lifecycleStatus?: string | null;
  onChanged?: (next: { id: string; slug: string; title: string } | null) => void;
  /** Visually compact (inbox rows) vs full (detail page). */
  compact?: boolean;
  className?: string;
}

export default function PrimaryStudySelect({ contactId, studyId, studyTitle, lifecycleStatus, onChanged, compact = false, className = '' }: Props) {
  const [studies, setStudies] = useState<StudyOption[]>([]);
  const [current, setCurrent] = useState<string>(studyId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  // A study change on a lead whose CURRENT stage is study-attributed waits here
  // for the "also fix reports?" decision.
  const [pendingNext, setPendingNext] = useState<string | null>(null);

  // Keep in sync if the parent re-resolves the contact (e.g. after a screener
  // auto-set the primary study).
  useEffect(() => { setCurrent(studyId || ''); }, [studyId]);

  useEffect(() => {
    let alive = true;
    api.get('/api/crm/studies')
      .then((res) => {
        if (alive) setStudies((res.data?.studies || []).filter((s: StudyOption) => s.id && s.source === 'crm'));
      })
      .catch(() => { /* the seeded label still renders the current study */ });
    return () => { alive = false; };
  }, []);

  const knownStudy = current ? studies.find((s) => s.id === current) : undefined;

  const save = async (next: string, reattribute: boolean) => {
    const prev = current;
    setCurrent(next);
    setSaving(true);
    setError(false);
    try {
      await api.patch(`/api/crm/contacts/${contactId}`, {
        primary_study_id: next || null,
        ...(reattribute ? { reattribute_current_stage: true } : {}),
      });
      const picked = next ? studies.find((s) => s.id === next) : null;
      onChanged?.(next ? { id: next, slug: picked?.slug || '', title: (picked && studyInternalLabel(picked)) || studyTitle || 'Study' } : null);
    } catch {
      setCurrent(prev);
      setError(true);
      setTimeout(() => setError(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const change = (next: string) => {
    if (next === current || saving) return;
    // The lead's current stage is a study-attributed outcome: moving their
    // study raises the "does the recorded outcome move too?" question.
    if (next && lifecycleStatus && STUDY_ATTRIBUTED_STAGES.includes(lifecycleStatus)) {
      setPendingNext(next);
      return;
    }
    void save(next, false);
  };

  const unset = !current;
  const stageLabel = lifecycleStatus ? lifecycleMeta(lifecycleStatus).label : '';
  const pendingStudy = pendingNext ? studies.find((s) => s.id === pendingNext) : undefined;

  return (
    <span className="relative inline-flex max-w-full items-center" onClick={(e) => e.stopPropagation()}>
      <FlaskConical className={`pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${unset ? 'text-amber-500' : 'text-primary-500'}`} />
      <select
        value={current}
        disabled={saving}
        onChange={(e) => change(e.target.value)}
        title={error ? 'Could not update the study — try again' : 'Study being considered'}
        aria-label="Primary study"
        className={`min-h-[44px] max-w-full cursor-pointer appearance-none truncate rounded-lg border bg-transparent font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-60 sm:min-h-0 ${
          compact ? 'py-1 pl-7 pr-6 text-xs' : 'py-1.5 pl-8 pr-8 text-sm'
        } ${
          unset
            ? 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'
            : 'border-gray-200 text-gray-700 dark:border-gray-600 dark:text-gray-200'
        } ${error ? 'ring-2 ring-red-400' : ''} ${className}`}
      >
        <option value="">No study</option>
        {studies.map((s) => (
          <option key={s.id} value={s.id!}>{studyInternalLabel(s)}</option>
        ))}
        {current && !knownStudy && (
          // The study isn't in the loaded list yet — show the seeded title.
          <option value={current}>{studyTitle || 'Selected study'}</option>
        )}
      </select>
      <svg
        className="pointer-events-none absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 opacity-50"
        viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
      >
        <path d="M3 4.5 6 7.5 9 4.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {pendingNext && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPendingNext(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Move recorded status with the study change"
          >
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              This lead is already “{stageLabel}”
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Changing the study to{' '}
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {(pendingStudy && studyInternalLabel(pendingStudy)) || 'the selected study'}
              </span>{' '}
              can also move their recorded “{stageLabel}” there, so the by-study
              reports count them under the right study. Pick “fix reports too” if
              the outcome really happened on the new study (e.g. they pivoted and
              were recorded under the old one).
            </p>
            <div className="mt-5 flex flex-col items-stretch justify-end gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => setPendingNext(null)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const next = pendingNext;
                  setPendingNext(null);
                  void save(next, false);
                }}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Just change study
              </button>
              <button
                type="button"
                onClick={() => {
                  const next = pendingNext;
                  setPendingNext(null);
                  void save(next, true);
                }}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Change + fix reports
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
